/**
 * Typed fetch wrapper for the gateway.
 *
 * Cookies are always sent (`credentials: 'include'`) — the gateway sets
 * httpOnly access_token and refresh_token cookies on the same origin.
 * For cross-origin dev (Vite on :5173, gateway on :8100) the gateway
 * already allows the right CORS origins.
 *
 * `ApiError` wraps non-2xx responses so callers can introspect the
 * gateway's error code without parsing JSON manually each time.
 */
import { env } from '../../env';

export class ApiError extends Error {
  status: number;
  code: string;
  detail?: unknown;
  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (detail !== undefined) this.detail = detail;
  }
}

interface RawErrorBody {
  error?: string;
  message?: string;
}

function joinPath(base: string, path: string): string {
  const trimmedBase = base.replace(/\/$/, '');
  if (path.startsWith('http')) return path;
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

export interface ApiRequestInit extends RequestInit {
  json?: unknown;
}

export async function apiFetch(path: string, init: ApiRequestInit = {}): Promise<Response> {
  const url = joinPath(env.VITE_API_BASE_URL, path);
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const { json, body: rawBody, ...rest } = init;
  let body: BodyInit | null | undefined = rawBody;
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }

  const requestInit: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
  };
  if (body !== undefined) requestInit.body = body;

  return fetch(url, requestInit);
}

export async function apiJson<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const response = await apiFetch(path, init);
  if (!response.ok) {
    let parsed: RawErrorBody = {};
    try {
      parsed = (await response.json()) as RawErrorBody;
    } catch {
      // Empty body or non-JSON — fall through to defaults.
    }
    throw new ApiError(
      response.status,
      parsed.error ?? `http_${response.status}`,
      parsed.message ?? response.statusText ?? 'Request failed',
      parsed,
    );
  }
  if (response.status === 204) return undefined as unknown as T;
  return (await response.json()) as T;
}
